import {GlobalAlertState} from "@/lib/types/alerting.ts";
import {DataPoint, Service, ServiceTag} from "@/lib/types/checks.ts";
import {MetricGranularity, MetricsExplorerResult} from "@/lib/types/metricsExplorer.ts";

export interface MultiSnippet {
  url: string;
  snippet1: string;
  snippet2: string;
}

export interface Snippets {
  rum2_snippet: string;
  webhook_snippet: MultiSnippet;
  heartbeat_snippet: MultiSnippet;
}

export interface DashboardDefinitionInput {
  id?: number;

  // Global
  name: string;
  is_pinned: boolean;
  services_selected: Service[];
  services_tags: ServiceTag[];
  match_all_tags: boolean;
  services_include_up: boolean;
  services_include_down: boolean;
  services_include_paused: boolean;
  services_include_maintenance: boolean;

  // Metrics
  metrics_show_section: boolean;
  metrics_for_all_checks: boolean;

  // Checks
  services_show_section: boolean;
  services_num_to_show: number;
  services_primary_sort: string;
  services_secondary_sort: string;
  services_show_uptime: boolean;
  services_show_response_time: boolean;

  // Alerts
  alerts_show_section: boolean;
  alerts_for_all_checks: boolean;
  alerts_include_ignored: boolean;
  alerts_include_resolved: boolean;
  alerts_num_to_show: number;

  // Charts
  charts_show_section: boolean;
  charts: DashboardChartInput[];
}

/** Rolling windows only, so the server picks the dates and every viewer shares a cache entry. */
export const DASHBOARD_CHART_TIME_RANGES = [
  "LAST_30_MINUTES",
  "LAST_1_HOUR",
  "LAST_4_HOURS",
  "LAST_12_HOURS",
  "LAST_24_HOURS",
  "LAST_7_DAYS",
  "LAST_14_DAYS",
  "LAST_30_DAYS",
] as const;
export type DashboardChartTimeRange = (typeof DASHBOARD_CHART_TIME_RANGES)[number];

export type DashboardChartStatus = "HIT" | "STALE" | "MISS" | "PENDING" | "ERROR";

/** Mirrors MAX_CHARTS_PER_DASHBOARD on the server. */
export const MAX_DASHBOARD_CHARTS = 12;

export interface DashboardChartInput {
  view_id: number;
  time_range: DashboardChartTimeRange;
}

export interface DashboardChart extends DashboardChartInput {
  id?: number;
  view_name?: string;
}

export interface DashboardChartCheckName {
  check_id: number;
  name: string;
}

export interface DashboardChartWidget {
  view_id: number;
  view_name: string | null;
  time_range: DashboardChartTimeRange;
  chart_type: string | null;
  status: DashboardChartStatus;
  start_date: string | null;
  end_date: string | null;
  granularity: MetricGranularity | null;
  computed_at: string | null;
  result: MetricsExplorerResult | null;
  check_names: DashboardChartCheckName[];
  error_code: string | null;
  error_message: string | null;
}

export interface DashboardDefinition extends DashboardDefinitionInput {
  ordering: number; // the actual order
  dashboard_position: number; // used for changing the order
  is_pinned: boolean;
  created_at?: Date;
  is_default_dashboard: boolean;
  services_selected: Service[];
  services_tags: ServiceTag[];
  charts: DashboardChart[];
}

export interface DashboardServiceSummary {
  total: number;
  paused: number;
  down: number;
  maintenance: number;
}

export interface DashboardOutageSummary {
  days_1: number;
  days_7: number;
  days_30: number;
  last: Date;
}

export interface DashboardUptimeSummary {
  uptime_pct: number;
  uptime_sla: number;
  downtime_secs: number;
}

export interface DashboardServiceTypeResponseTime {
  response_time_avg: number;
  response_time_max: number;
  response_time_sla: number;
}

export interface DashboardServiceTypeResponseTimes {
  API: DashboardServiceTypeResponseTime;
  HTTP: DashboardServiceTypeResponseTime;
  RUM2: DashboardServiceTypeResponseTime;
  TRANSACTION: DashboardServiceTypeResponseTime;
}

export interface DashboardMetrics {
  global_metrics: DashboardGlobalMetrics;
  latest_outages: GlobalAlertState[];
}

export interface DashboardGlobalMetrics {
  checks_are_filtered: boolean;
  checks: DashboardServiceSummary;
  outages: DashboardOutageSummary;
  uptime: DashboardUptimeSummary;
  response_time: DashboardServiceTypeResponseTimes;
}

export interface ServicePerformanceMetric {
  service_id: number;
  uptime_24h: number;
  response_time_datapoints: DataPoint[];
}

export interface DashboardServiceInfo {
  service_info: Service[];
  service_total: number;
  service_performance_metrics: ServicePerformanceMetric[];
}

export interface ChangelogEntry {
  id: number;
  date: Date;
  isPublished: boolean;
  url: string;
  title: string;
  content: string;
}
